import { apiUrl } from './platform'

const MAX_RETRIES = 2
const INITIAL_DELAY_MS = 1000

function isRetryable(status) {
  return status === 429 || status >= 500
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Calls the server-side API to decompose a task using AI.
 * Retries up to 2 times on 429/5xx errors with exponential backoff.
 * Returns an array of { title, timeEstimate } objects.
 * Throws on failure — caller should handle fallback.
 */
export async function decomposeTask({ title, description, dueDate, priority, size }) {
  let lastError

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await delay(INITIAL_DELAY_MS * attempt)
    }

    const response = await fetch(apiUrl('/api/decompose'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, description, dueDate, priority, size }),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      lastError = new Error(err.error || `API error: ${response.status}`)
      lastError.status = response.status

      if (isRetryable(response.status) && attempt < MAX_RETRIES) {
        continue
      }
      throw lastError
    }

    const data = await response.json()

    if (!Array.isArray(data.subtasks) || data.subtasks.length === 0) {
      throw new Error('No subtasks returned')
    }

    return data.subtasks
  }

  throw lastError
}

/**
 * Calls the server-side API to modify a task breakdown via chat.
 * Returns { type: 'subtasks'|'question', subtasks: [...]|null, message: '...' }
 */
export async function chatTaskBreakdown({ userMessage, task, chatHistory }) {
  let lastError

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await delay(INITIAL_DELAY_MS * attempt)
    }

    const response = await fetch(apiUrl('/api/chat-task'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userMessage, task, chatHistory }),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      lastError = new Error(err.error || `API error: ${response.status}`)
      lastError.status = response.status

      if (isRetryable(response.status) && attempt < MAX_RETRIES) {
        continue
      }
      throw lastError
    }

    const data = await response.json()

    if (!data || !data.type || !data.message) {
      throw new Error('Invalid response from AI')
    }

    return data
  }

  throw lastError
}
